import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import {
  Box,
  TextField,
  Grid,
  Autocomplete,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ServiceLocation } from '../../../data/types/domain';
import { supabase } from '../../../config/supabase';
import { TABLES } from '../../../data/tables';
import { useNotification, AppNotification } from '../../../components/Notification';
import { FormikProps } from 'formik';
import * as Yup from 'yup';
import { useFormContext } from '../context/FormContext';
import { locationAPIService } from '../../../services/locationAPIService';
import debounce from 'lodash/debounce';

interface ServiceLocationStepProps {
  formik?: FormikProps<any>;
  initialData?: Omit<ServiceLocation, "id" | "created_at" | "updated_at" | "business_id">[];
}

const validationSchema = Yup.object().shape({
  serviceLocation: Yup.array()
    .min(1, 'At least one service location is required')
    .of(
      Yup.object().shape({
        country: Yup.number().required('Country is required'),
        city: Yup.number().nullable().optional()
      })
    ),
});

export interface ServiceLocationStepRef {
  isValid: () => Promise<boolean>;
  getErrors: () => Promise<string[]>;
  getData: () => Promise<any>;
}

interface SearchOption {
  type: 'country' | 'city';
  name: string;
  countryName?: string;
}

export const ServiceLocationStep = forwardRef<ServiceLocationStepRef, ServiceLocationStepProps>(({ formik, initialData }, ref) => {
  const [values, setValues] = useState(initialData || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<SearchOption | null>(null);
  const [newLocation, setNewLocation] = useState<Omit<ServiceLocation, 'id' | 'created_at' | 'updated_at' | 'business_id'>>({
    country: 0,
    city: null
  });

  const { notification, showNotification, hideNotification } = useNotification();
  const { countries, cities, isLoading, getCitiesByCountry, getCountryName, getCityName, addCountry, addCity } = useFormContext();

  useImperativeHandle(ref, () => ({
    isValid: async () => {
      try {
        await validationSchema.validate({ serviceLocation: values }, { abortEarly: false });
        return true;
      } catch (err) {
        return false;
      }
    },
    getErrors: async () => {
      try {
        await validationSchema.validate({ serviceLocation: values }, { abortEarly: false });
        return [];
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          return err.errors;
        }
        return ['An unknown error occurred'];
      }
    },
    getData: async () => {
      return values;
    }
  }));

  // Ensure serviceLocation is an array
  useEffect(() => {
    if (!Array.isArray(values)) {
      setValues([]);
    }
  }, [values, setValues]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (term: string) => {
        if (term.length < 3) {
          setSearchResults([]);
          return;
        }

        setLoading(true);
        try {
          const results = await locationAPIService.searchLocations(term);
          const options: SearchOption[] = [
            ...results.countries.map((country): SearchOption => ({
              type: 'country',
              name: country,
            })),
            ...results.cities.map((city): SearchOption => ({
              type: 'city',
              name: city.name,
              countryName: city.country,
            })),
          ];
          setSearchResults(options);
        } catch (error) {
          console.error('Search error:', error);
          showNotification('Error searching locations', 'error');
        } finally {
          setLoading(false);
        }
      }, 300),
    [showNotification]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => debouncedSearch.cancel();
  }, [searchTerm]);

  const handleSearch = async (option: SearchOption | null) => {
    if (!option) return;

    try {
      if (option.type === 'country') {
        // Check if country exists
        const { data: existingCountry } = await supabase
          .from(TABLES.COUNTRY)
          .select()
          .ilike('name', option.name)
          .single();

        if (existingCountry) {
          setNewLocation(prev => ({ ...prev, country: existingCountry.id }));
        } else {
          // Create new country
          const { data: newCountry, error } = await supabase
            .from(TABLES.COUNTRY)
            .insert([{ name: option.name }])
            .select()
            .single();

          if (error) throw error;
          addCountry(newCountry);
          setNewLocation(prev => ({ ...prev, country: newCountry.id }));
        }
      } else {
        // Check if city exists
        const { data: existingCity } = await supabase
          .from(TABLES.CITY)
          .select('*, country:country_id(*)')
          .ilike('name', option.name)
          .eq('country.name', option.countryName)
          .single();

        if (existingCity) {
          setNewLocation(prev => ({
            ...prev,
            country: existingCity.country_id,
            city: existingCity.id
          }));
        } else {
          // First ensure country exists
          const { data: country } = await supabase
            .from(TABLES.COUNTRY)
            .select()
            .ilike('name', option.countryName!)
            .single();

          let countryId = country?.id;

          if (!countryId) {
            // Create country first
            const { data: newCountry } = await supabase
              .from(TABLES.COUNTRY)
              .insert([{ name: option.countryName }])
              .select()
              .single();
            countryId = newCountry.id;
            addCountry(newCountry);
          }

          // Create city
          const { data: newCity, error } = await supabase
            .from(TABLES.CITY)
            .insert([{
              name: option.name,
              country_id: countryId
            }])
            .select()
            .single();

          if (error) throw error;
          addCity(newCity);
          setNewLocation(prev => ({
            ...prev,
            country: countryId,
            city: newCity.id
          }));
        }
      }

      setSelectedOption(option);
    } catch (error) {
      console.error('Error processing selection:', error);
      showNotification('Error processing selection', 'error');
    }
  };

  const handleAddLocation = () => {
    if (!newLocation.country) {
      showNotification('Please select a location', 'error');
      return;
    }

    // Create location object based on selection type
    const locationToAdd = selectedOption?.type === 'country' 
      ? { country: newLocation.country, city: null } // Country only, explicitly set city to null
      : { country: newLocation.country, city: newLocation.city }; // Country and city

    // Check for duplicates based on selection type
    const isDuplicate = values.some(location => {
      if (selectedOption?.type === 'country') {
        // For country selections, check only country
        return location.country === locationToAdd.country;
      } else {
        // For city selections, check both country and city
        return location.country === locationToAdd.country && location.city === locationToAdd.city;
      }
    });

    if (isDuplicate) {
      showNotification('This location is already added', 'error');
      return;
    }

    setValues([...values, locationToAdd]);
    setNewLocation({ country: 0, city: null }); // Reset with null city
    setSelectedOption(null);
    setSearchTerm('');
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Search and select locations where your business operates. Type at least 3 characters to search.
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Autocomplete
            value={selectedOption}
            onChange={(_, newValue) => handleSearch(newValue)}
            options={searchResults}
            getOptionLabel={(option) => option.type === 'city' ? `${option.name}, ${option.countryName}` : option.name}
            groupBy={(option) => option.type === 'country' ? 'Countries' : 'Cities'}
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search locations"
                onChange={(e) => setSearchTerm(e.target.value)}
                value={searchTerm}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                helperText={searchTerm.length > 0 && searchTerm.length < 3 ? 'Type at least 3 characters to search' : ''}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography>
                  {option.type === 'city' 
                    ? `${option.name}, ${option.countryName}`
                    : option.name}
                </Typography>
              </li>
            )}
            noOptionsText={
              searchTerm.length < 3 
                ? "Type at least 3 characters to search"
                : "No locations found"
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddLocation}
            disabled={!selectedOption}
            fullWidth
          >
            Add Service Location
          </Button>
        </Grid>
      </Grid>

      <Typography variant="subtitle1" gutterBottom>
        Added Service Locations
      </Typography>

      {!values.length ? (
        <Typography variant="body2" color="text.secondary">
          No service locations added yet. Add at least one service location.
        </Typography>
      ) : (
        <List>
          {values.map((location, index) => {
            const country = countries.find(c => c.id === location.country);
            const city = cities.find(c => c.id === location.city);
            return (
              <ListItem key={index} divider>
                <ListItemText
                  primary={country?.name}
                  secondary={location.city ? city?.name : 'Entire Country'}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => {
                      const newValues = values.filter((_, i) => i !== index);
                      setValues(newValues);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Add the Notification component */}
      <AppNotification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </Box>
  );
}); 